import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import ApiMessageHistoryCard from "../../../Components/Common/ApiMessageHistoryCard";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import {
  apiErrorMessage,
  getWelcomeAutomation,
  updateWelcomeAutomation,
  WelcomeAutomation,
} from "../../../helpers/automations_helper";
import {
  Campaign,
  getCampaign,
  getCampaigns,
} from "../../../helpers/campaigns_helper";
import { htmlHasUnsubMarker } from "../../../helpers/api_messages_helper";

type CampaignOption = { value: number; label: string };

/**
 * Welcome opt-in: desligado por padrão — Carla liga e escolhe a campanha (DOC 03 § 4.9).
 */
const WelcomeAutomationPage = () => {
  const { t } = useTranslation();
  document.title = t("automations.welcome.documentTitle");

  const helpHref = helpUrlForRoute("/automations/welcome");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<WelcomeAutomation | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [delayMinutes, setDelayMinutes] = useState(0);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [welcomeRes, list] = await Promise.all([
        getWelcomeAutomation(),
        getCampaigns({ has_html: true, per_page: 100 }),
      ]);
      const welcome = welcomeRes.data;
      setConfig(welcome);
      setEnabled(!!welcome.enabled);
      setCampaignId(welcome.campaign_id);
      setDelayMinutes(welcome.delay_minutes ?? 0);
      setCampaigns(list.data || []);
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.welcome.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Preview do HTML da campanha escolhida — Carla confere o conteúdo antes de salvar.
  useEffect(() => {
    if (!campaignId) {
      setPreviewHtml(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    void getCampaign(campaignId)
      .then((res) => {
        if (cancelled) return;
        const html = res.data?.html_body || "";
        setPreviewHtml(html.trim() ? html : null);
        if (!html.trim()) {
          setPreviewError(t("automations.welcome.previewEmpty"));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewHtml(null);
        setPreviewError(
          apiErrorMessage(e, t("automations.welcome.previewError"))
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, t]);

  const options: CampaignOption[] = useMemo(
    () =>
      campaigns.map((c) => ({
        value: c.id,
        label: `${c.name} — ${c.subject || t("automations.welcome.noSubject")}`,
      })),
    [campaigns, t]
  );

  const selected = options.find((o) => o.value === campaignId) || null;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateWelcomeAutomation({
        enabled,
        campaign_id: campaignId,
        delay_minutes: delayMinutes,
      });
      const next = res.data;
      setConfig(next);
      setEnabled(!!next.enabled);
      setCampaignId(next.campaign_id);
      setDelayMinutes(next.delay_minutes ?? 0);
      toast.success(t("automations.welcome.saved"));
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.welcome.saveError")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("automations.welcome.title")}
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink
                href={helpHref}
                label={t("automations.welcome.help")}
              />
            ) : undefined
          }
        />
        <DismissiblePageTip tipId="welcome-automation-intro">
          {t("automations.welcome.tip")}
        </DismissiblePageTip>

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                {loading || !config ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <>
                    {options.length === 0 ? (
                      <Alert color="info" className="mb-3">
                        {t("automations.welcome.noCampaignsHint")}{" "}
                        <Link to="/campaigns/new">
                          {t("automations.welcome.createCampaignCta")}
                        </Link>
                      </Alert>
                    ) : null}

                    <FormGroup switch className="mb-4">
                      <Input
                        type="switch"
                        role="switch"
                        id="welcome-enabled"
                        checked={enabled}
                        disabled={saving}
                        onChange={(e) => setEnabled(e.target.checked)}
                      />
                      <Label check for="welcome-enabled" className="ms-2">
                        {t("automations.welcome.enabledLabel")}
                      </Label>
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.welcome.enabledHelp")}
                      </p>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label>{t("automations.welcome.campaignLabel")}</Label>
                      <Select
                        options={options}
                        value={selected}
                        onChange={(opt: CampaignOption | null) =>
                          setCampaignId(opt ? opt.value : null)
                        }
                        isClearable
                        isDisabled={saving}
                        placeholder={t(
                          "automations.welcome.campaignPlaceholder"
                        )}
                        classNamePrefix="select"
                        noOptionsMessage={() =>
                          t("automations.welcome.noCampaigns")
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.welcome.campaignHelp")}
                      </p>
                    </FormGroup>

                    {campaignId ? (
                      <FormGroup className="mb-4">
                        <Label>{t("automations.welcome.previewLabel")}</Label>
                        {previewLoading ? (
                          <div className="text-center py-3 border rounded">
                            <Spinner size="sm" color="primary" />
                          </div>
                        ) : previewError && !previewHtml ? (
                          <Alert color="warning" className="mb-0">
                            {previewError}
                          </Alert>
                        ) : previewHtml ? (
                          <div
                            className="border rounded p-3 bg-light"
                            style={{
                              maxHeight: 280,
                              overflow: "auto",
                            }}
                          >
                            {/* sandboxed: só leitura do HTML da campanha do próprio tenant */}
                            <iframe
                              title={t("automations.welcome.previewLabel")}
                              srcDoc={previewHtml}
                              sandbox=""
                              style={{
                                width: "100%",
                                height: 240,
                                border: 0,
                                background: "#fff",
                              }}
                            />
                          </div>
                        ) : null}
                        {previewHtml && !htmlHasUnsubMarker(previewHtml) ? (
                          <Alert color="warning" className="mt-2 mb-0">
                            {t("automations.welcome.unsubMissingWarn")}{" "}
                            <code className="user-select-all">
                              {"{{brenvio_track_unsub}}"}
                            </code>
                          </Alert>
                        ) : null}
                      </FormGroup>
                    ) : null}

                    <FormGroup className="mb-4">
                      <Label for="welcome-delay">
                        {t("automations.welcome.delayLabel")}
                      </Label>
                      <Input
                        id="welcome-delay"
                        type="number"
                        min={0}
                        max={10080}
                        value={delayMinutes}
                        disabled={saving}
                        onChange={(e) =>
                          setDelayMinutes(
                            Math.max(0, Math.min(10080, Number(e.target.value) || 0))
                          )
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.welcome.delayHelp")}
                      </p>
                    </FormGroup>

                    <Button
                      color="primary"
                      disabled={saving}
                      onClick={() => void onSave()}
                    >
                      {saving ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      {t("automations.welcome.save")}
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>

            <ApiMessageHistoryCard
              source="welcome"
              titleKey="apiMessages.history.welcomeTitle"
              hintKey="apiMessages.history.welcomeHint"
              emptyCtaTo="/campaigns/new"
              emptyCtaLabelKey="apiMessages.history.welcomeEmptyCta"
              emptyHintKey="apiMessages.history.welcomeEmpty"
            />
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default WelcomeAutomationPage;
